import ReportChartPage, {
  ReportChartConfig,
} from '@app/components/reports/components/report-chart-page';
import {
  carbonIntensityAxisProps,
  carbonIntensityChartConfig,
  emissionsChartConfig,
  emissionsWithCarbonIntensityConfig,
  emissionsWithGoodsTypeConfig,
  totalEmissionsAxisProps,
} from '@app/components/reports/config';
import { ReportSectionComponent } from '@app/components/reports/types';
import {
  useEmissionsChartVariants,
  useGetReportSectionData,
  useGetReportTypeCopy,
} from '@app/components/reports/utils';
import DualAxisBarChart from '@shared/components/chart/dual-axis-bar-chart';
import SimpleBarChart from '@shared/components/chart/simple-bar-chart';
import {
  getXAxisInterval,
  truncateLabel,
} from '@shared/components/chart/utils';
import Text from '@shared/components/content/text';
import { SalesReportResultData } from '@shared/api/types';

interface EmissionsByCustomerProps extends ReportSectionComponent {}

/**
 * Sales-report sibling of EmissionsBySupplier. Spend is not captured for sold
 * goods, so the "Emissions vs spend" variant is not offered.
 */
export default function EmissionsByCustomer({
  reportType,
}: EmissionsByCustomerProps) {
  const { result, downloadMode } = useGetReportSectionData(reportType);
  const { emissionsByCustomer } = result as SalesReportResultData;
  const reportTypeCopy = useGetReportTypeCopy(reportType);
  const { variant, setVariant, availableVariants, variants } =
    useEmissionsChartVariants(reportType, 'customer');

  const variantsWithoutSpend = availableVariants.filter(
    (availableVariant) => availableVariant !== variants.spend
  );

  const emissionsWithCarbonIntensityData = emissionsByCustomer;
  const emissionsWithGoodsType = emissionsByCustomer.map((item) => {
    const result: Record<string, number | string> = { name: item.name };
    item.byGoodsType.forEach((type) => {
      result[type.name.toLocaleLowerCase()] = type.emissionsTonnes;
    });
    return result;
  });

  const emissionsDataMax = Math.max(
    0,
    ...emissionsWithCarbonIntensityData.map(
      (d: { emissionsTonnes: number }) => d.emissionsTonnes
    )
  );
  const carbonIntensityDataMax = Math.max(
    0,
    ...emissionsWithCarbonIntensityData.map(
      (d: { carbonIntensity: number }) => d.carbonIntensity
    )
  );

  const defaultChartProps = {
    nameKey: 'name',
    dataKey: 'name',
    margin: { top: 40, right: 10, left: 0, bottom: 60 },
    className: 'h-max max-h-[calc(var(--content-title-height)-20rem)] w-full',
    XAxisProps: {
      interval: 0,
      angle: -30,
      textAnchor: 'end',
      tickMargin: 2,
      dx: 10,
      tickFormatter: (value: string) => truncateLabel(value, 15),
    },
  };

  let config: ReportChartConfig = emissionsChartConfig;
  if (variant === variants.goodsType) {
    config = emissionsWithGoodsTypeConfig;
  } else if (variant === variants.intensity) {
    config = emissionsWithCarbonIntensityConfig;
  }

  return (
    <ReportChartPage
      title="Total emissions per customer"
      description={
        <Text variant="reportBody">
          This chart shows the total emissions from each of your customers,
          ordered by highest emissions. Emissions from {reportTypeCopy.plural}{' '}
          not associated with a customer are grouped under <em>No customer</em>.
        </Text>
      }
      variantOptions={variantsWithoutSpend}
      variant={variant}
      setVariant={setVariant}
      variantsEnabled={!downloadMode && variantsWithoutSpend.length > 1}
      chartConfig={config}
    >
      {variant === variants.emissions && (
        <SimpleBarChart
          {...defaultChartProps}
          chartConfig={emissionsChartConfig}
          chartData={emissionsWithCarbonIntensityData}
          YAxisProps={totalEmissionsAxisProps(emissionsDataMax)}
          XAxisProps={{
            ...defaultChartProps.XAxisProps,
            interval: getXAxisInterval(emissionsWithCarbonIntensityData.length),
          }}
        />
      )}
      {variant === variants.intensity && (
        <DualAxisBarChart
          {...defaultChartProps}
          leftAxisConfig={emissionsChartConfig}
          rightAxisConfig={carbonIntensityChartConfig}
          chartData={emissionsWithCarbonIntensityData}
          LeftYAxisProps={totalEmissionsAxisProps(emissionsDataMax)}
          RightYAxisProps={carbonIntensityAxisProps(carbonIntensityDataMax)}
          XAxisProps={{
            ...defaultChartProps.XAxisProps,
            interval: getXAxisInterval(emissionsWithCarbonIntensityData.length),
          }}
        />
      )}
      {variant === variants.goodsType && (
        <SimpleBarChart
          {...defaultChartProps}
          legend={false}
          chartData={emissionsWithGoodsType}
          chartConfig={emissionsWithGoodsTypeConfig}
          YAxisProps={totalEmissionsAxisProps(emissionsDataMax)}
          XAxisProps={{
            ...defaultChartProps.XAxisProps,
            interval: getXAxisInterval(emissionsWithGoodsType.length),
          }}
        />
      )}
    </ReportChartPage>
  );
}
